import { FC } from 'react';
import { Flex, Skeleton } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { SmallResourceFooter } from '@/shared/components/layouts/footers';
import {
    AvatarSkeleton,
    BaseSkeleton,
    ButtonSkeleton,
    ChartSkeleton,
    SkeletonFade,
} from '@/shared/components/skeletons';

interface FinancialOverviewResourceSkeletonProps {
    resourceLabel?: string;
    tabCount?: number;
}

const ResourceSkeletonSectionHeader: FC<{ width?: number }> = ({ width = 180 }) => (
    <Flex align='center' gap='0.75rem'>
        <Skeleton.Avatar active shape='square' size={32} />
        <Flex gap='0.375rem' vertical>
            <Skeleton.Input active size='small' style={{ height: '1.125rem', width }} />
            <Skeleton.Input active size='small' style={{ height: '0.75rem', width: width * 1.8 }} />
        </Flex>
    </Flex>
);

const ResourceSkeletonSectionCard: FC = () => {
    const { token } = useAntDesignTheme();

    return (
        <div
            style={{
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadiusLG,
                minHeight: '9.5rem',
                padding: '1rem',
            }}
        >
            <ResourceSkeletonSectionHeader width={170} />
            <Skeleton
                active
                paragraph={{ rows: 3, width: ['100%', '96%', '72%'] }}
                style={{ marginTop: '1.25rem' }}
                title={false}
            />
            <Flex gap='0.5rem' style={{ marginTop: '0.75rem' }}>
                {Array.from({ length: 6 }, (_, index) => (
                    <Skeleton.Avatar active key={index} shape='square' size={20} />
                ))}
            </Flex>
        </div>
    );
};

/**
 * Shared full-page loading shell for resources backed by the financial overview.
 * Keeping the shell here prevents Search and Investments from drifting visually.
 */
export const FinancialOverviewResourceSkeleton: FC<FinancialOverviewResourceSkeletonProps> = ({
    resourceLabel = 'Company',
    tabCount = 10,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <div
            aria-label={`Loading ${resourceLabel} Overview`}
            aria-live='polite'
            role='status'
            style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
        >
            <Flex
                align='center'
                gap='0.75rem'
                justify='space-between'
                style={{ borderBottom: `1px solid ${token.colorBorder}`, minHeight: '3.25rem', padding: '0 1.5rem' }}
            >
                <Flex align='center' gap='0.625rem' style={{ minWidth: 0 }}>
                    <Skeleton.Input active size='small' style={{ height: '1rem', width: 58 }} />
                    <Skeleton.Input active size='small' style={{ height: '1rem', width: 72 }} />
                    <Skeleton.Avatar active shape='square' size={28} />
                    <Skeleton.Input active size='small' style={{ height: '1.25rem', width: 190 }} />
                    <Skeleton.Button active size='small' style={{ width: 72 }} />
                    <Skeleton.Button active size='small' style={{ width: 68 }} />
                </Flex>
                <Flex gap='0.5rem'>
                    <Skeleton.Button active size='small' style={{ width: 126 }} />
                    <Skeleton.Button active size='small' style={{ width: 108 }} />
                </Flex>
            </Flex>

            <Flex
                align='center'
                gap='1rem'
                style={{
                    borderBottom: `1px solid ${token.colorBorder}`,
                    minHeight: '2.5rem',
                    overflow: 'hidden',
                    padding: '0 1rem',
                }}
            >
                {Array.from({ length: tabCount }, (_, index) => (
                    <Skeleton.Input
                        active
                        key={index}
                        size='small'
                        style={{ height: '0.875rem', width: index === 0 ? 72 : 88 }}
                    />
                ))}
            </Flex>

            <SkeletonFade
                style={{
                    flex: '1 1 0%',
                    minHeight: 0,
                    overflow: 'hidden',
                    padding: '0.75rem',
                }}
            >
                <Flex gap='0.75rem' vertical>
                    {Array.from({ length: 8 }, (_, index) => (
                        <ResourceSkeletonSectionCard key={index} />
                    ))}
                </Flex>
            </SkeletonFade>
            <SmallResourceFooter
                center={<Skeleton.Input active size='small' style={{ height: '0.75rem', width: 132 }} />}
                right={<Skeleton.Avatar active shape='square' size={20} />}
                showHelp={false}
            />
        </div>
    );
};

const MetricCardSkeleton: FC = () => (
    <div style={{ minWidth: 0, padding: '0.75rem' }}>
        <ButtonSkeleton block size='small' style={{ display: 'block', height: '0.75rem', width: '55%' }} />
        <ButtonSkeleton
            block
            size='small'
            style={{ display: 'block', height: '1.25rem', marginTop: '0.5rem', width: '72%' }}
        />
    </div>
);

export const FinancialOverviewIdentitySkeleton: FC = () => (
    <div aria-busy='true' aria-label='Loading identity'>
        <ButtonSkeleton
            block
            shape='default'
            style={{ display: 'block', height: '12rem', margin: '1rem', width: 'calc(100% - 2rem)' }}
        />
        <div
            style={{
                alignItems: 'flex-start',
                display: 'flex',
                gap: '1rem',
                marginTop: '-4.75rem',
                padding: '0 2rem 1rem',
                position: 'relative',
            }}
        >
            <AvatarSkeleton shape='square' size={112} />
            <div style={{ flex: 1, minWidth: 0, paddingTop: '5rem' }}>
                <ButtonSkeleton block style={{ display: 'block', height: '1.5rem', width: '32%' }} />
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                    {[28, 18, 22, 16].map((width, index) => (
                        <ButtonSkeleton
                            block
                            key={index}
                            size='small'
                            style={{ display: 'block', height: '0.875rem', width: `${width}%` }}
                        />
                    ))}
                </div>
            </div>
            <ButtonSkeleton size='small' style={{ marginTop: '5rem', width: '7.5rem' }} />
        </div>
    </div>
);

export const FinancialOverviewChartSkeleton: FC = () => (
    <div aria-busy='true' aria-label='Loading market performance'>
        <div style={{ alignItems: 'center', display: 'flex', gap: '0.5rem', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <ButtonSkeleton size='small' style={{ width: '6.5rem' }} />
                <ButtonSkeleton size='small' style={{ width: '7.5rem' }} />
                <ButtonSkeleton size='small' style={{ width: '6.5rem' }} />
            </div>
            <ButtonSkeleton size='small' style={{ width: '28rem' }} />
        </div>
        <div style={{ margin: '0.75rem -1.25rem 1rem', padding: '0.5rem 1.25rem' }}>
            <ButtonSkeleton size='small' style={{ width: '12rem' }} />
        </div>
        <ChartSkeleton />
    </div>
);

export const FinancialOverviewStatisticsSkeleton: FC<{ detailRows?: number }> = ({ detailRows = 8 }) => (
    <div aria-busy='true' aria-label='Loading statistics'>
        <div
            style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
            }}
        >
            {Array.from({ length: 4 }, (_, index) => (
                <MetricCardSkeleton key={index} />
            ))}
        </div>
        {detailRows > 0 ? (
            <div style={{ marginTop: '1rem' }}>
                <BaseSkeleton paragraph={{ rows: detailRows }} title={false} />
            </div>
        ) : null}
    </div>
);

export const FinancialOverviewMapSkeleton: FC = () => (
    <div aria-busy='true' aria-label='Loading location' style={{ height: '18rem', padding: '1rem' }}>
        <ChartSkeleton />
    </div>
);
